/**
 * Supabase Context Provider con Auth0 Integration
 *
 * Este provider crea un cliente Supabase autenticado que:
 * 1. Intercepta todas las peticiones para agregar el token de Auth0
 * 2. Sincroniza el token con el cliente global (Alternativa B temporal)
 * 3. Mantiene un cliente ESTABLE (no se recrea en token refresh)
 * 4. Actualiza Realtime WebSocket via setAuth() en cada refresh
 *
 * Uso en componentes:
 * ```typescript
 * const { supabase, isLoading } = useSupabase()
 * await clientesService.listar(supabase, filtros)
 * ```
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { useAuth0 } from '@auth0/auth0-react'
import type { Database } from '@/types/database'
import { assertSupabaseRoleClaim, setGlobalSupabaseToken } from './supabase'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Códigos con los que Auth0 avisa que ya no hay sesión: el refresh token venció, fue
// revocado, o nunca llegó a guardarse. Un fallo de red o un 500 del tenant NO entran acá
// — ahí reintentar puede funcionar y expulsar al usuario sería peor que esperar.
const SESSION_ERROR_CODES = [
  'login_required',
  'consent_required',
  'invalid_grant',
  'missing_refresh_token',
]

/**
 * Distingue "la sesión murió" de cualquier otro fallo al pedir el token.
 *
 * El SDK de Auth0 no expone un tipo estable para esto: tira `MissingRefreshTokenError`
 * con `error: 'missing_refresh_token'`, y para el rechazo del server
 * (`Unknown or invalid refresh token`) un `GenericError` con `error: 'invalid_grant'`.
 * Por eso se mira el código y, como red de seguridad, el mensaje.
 */
export function isSessionExpiredError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false

  const codigo = (error as { error?: unknown }).error
  if (typeof codigo === 'string' && SESSION_ERROR_CODES.includes(codigo)) return true

  const mensaje = (error as { message?: unknown }).message
  return typeof mensaje === 'string' && /(missing|invalid) refresh token/i.test(mensaje)
}

interface SupabaseContextValue {
  supabase: SupabaseClient<Database> | null
  isLoading: boolean
  /**
   * La sesión de Auth0 murió. Hay que mirarlo aparte de `isAuthenticated`: con
   * `cacheLocation="localstorage"` el SDK sigue afirmando que hay sesión aunque el
   * refresh token esté revocado, y sin esta señal la app degradaba a "sin permisos" y
   * mostraba "Acceso Denegado" en vez de mandar al login (ORR-236).
   */
  sessionExpired: boolean
}

const SupabaseContext = createContext<SupabaseContextValue>({
  supabase: null,
  isLoading: true,
  sessionExpired: false,
})

// Create client ONCE (stable reference — never recreated)
// Token is injected dynamically via fetch interceptor + setAuth for Realtime
function createStableClient(getToken: () => Promise<string>): SupabaseClient<Database> | null {
  if (!supabaseUrl || !supabaseAnonKey) {
    console.warn('Supabase no está configurado')
    return null
  }

  return createClient<Database>(supabaseUrl, supabaseAnonKey, {
    // La auth real es Auth0 (token fresco por request vía fetch interceptor), no GoTrue.
    // storageKey propio distinto al del cliente global (supabase.ts) → evita el warning
    // "Multiple GoTrueClient instances" (el contador de auth-js es por storageKey).
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      storageKey: 'sb-brainhub-auth',
    },
    global: {
      // Sin token NO se emite la request. Mandarla igual la vuelve anónima, y como el RLS
      // de BrainHub exige el claim `role=authenticated`, PostgREST responde 200 con 0
      // filas en vez de 401: la app leía eso como "este usuario no tiene nada" y terminaba
      // en "Acceso Denegado". Propagar el error es lo que permite distinguir los casos
      // (ORR-236). Un token sin el claim `role` tampoco emite la request.
      fetch: async (url, options = {}) => {
        const token = await getToken()
        assertSupabaseRoleClaim(token)
        const headers = new Headers(options.headers)
        headers.set('Authorization', `Bearer ${token}`)
        return fetch(url, { ...options, headers })
      },
    },
  })
}

export function SupabaseProvider({ children }: { children: ReactNode }) {
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0()

  // Stable ref for token getter (avoids recreating client on re-renders)
  const getTokenRef = useRef(getAccessTokenSilently)
  // eslint-disable-next-line react-hooks/refs -- stable callback ref pattern: el client supabase NO debe recrearse cuando getAccessTokenSilently cambia (lo que ocurre en cada render de Auth0); el ref permite leer el valor actual sin invalidar useEffect ni recrear el cliente
  getTokenRef.current = getAccessTokenSilently

  const [sessionExpired, setSessionExpired] = useState(false)

  // Único punto donde se pide el token: si la sesión murió queda registrado en el contexto
  // antes de propagar el error, así el guard de la app puede mandar al login (ORR-236).
  const getToken = useCallback(async () => {
    try {
      return await getTokenRef.current()
    } catch (error) {
      if (isSessionExpiredError(error)) setSessionExpired(true)
      throw error
    }
  }, [])

  // Create client ONCE and keep stable reference
  const clientRef = useRef<SupabaseClient<Database> | null>(null)
  const [clientReady, setClientReady] = useState(false)

  // Initialize client on first auth
  useEffect(() => {
    if (isAuthenticated && !clientRef.current) {
      clientRef.current = createStableClient(getToken)
      setClientReady(true)
    }
  }, [isAuthenticated, getToken])

  // Token refresh: update global token + Realtime WebSocket auth
  useEffect(() => {
    if (!isAuthenticated) {
      setGlobalSupabaseToken(null)
      return
    }

    let mounted = true

    const refreshToken = async () => {
      try {
        const token = await getToken()
        if (!mounted) return

        // Update global token for legacy services
        setGlobalSupabaseToken(token)

        // NOTE: Do NOT call realtime.setAuth(token) with Auth0 JWT.
        // Auth0 JWT has `role` nested in 'https://supabase.io/jwt/claims'
        // but Supabase Realtime expects `role` at root level.
        // Realtime works fine with anon key + RLS check via fetch interceptor.
        // See: InvalidJWTToken: Fields `role` and `exp` are required in JWT
      } catch (error) {
        console.error('Error refreshing Supabase token:', error)
        if (mounted) setGlobalSupabaseToken(null)
      }
    }

    // Refresh immediately + every 50 minutes
    refreshToken()
    const interval = setInterval(refreshToken, 50 * 60 * 1000)

    return () => {
      mounted = false
      clearInterval(interval)
      setGlobalSupabaseToken(null)
    }
  }, [isAuthenticated, getToken])

  return (
    <SupabaseContext.Provider
      value={{
        // eslint-disable-next-line react-hooks/refs -- stable callback ref: clientRef se inicializa en effect y permanece estable hasta logout
        supabase: isAuthenticated ? clientRef.current : null,
        isLoading: isLoading || (isAuthenticated && !clientReady),
        sessionExpired,
      }}
    >
      {children}
    </SupabaseContext.Provider>
  )
}

export function useSupabase() {
  const context = useContext(SupabaseContext)
  if (context === undefined) {
    throw new Error('useSupabase must be used within SupabaseProvider')
  }
  return context
}
